// HUD가 실제로 차지한 화면 영역(CSS px, #ui·#game 공통 좌표: 화면 왼쪽 위 원점)의 단일 소스.
// HUD만 쓰고(대상 프레임·보스 바), 다른 층(VFX 피해 숫자)은 읽기만 한다. 고정 슬롯 + typed array라 읽기·쓰기에 객체를 만들지 않는다.
// 크기는 HUD의 초기 보호 치수 또는 ResizeObserver 관측값, 위치는 HUD의 top·배율에서 나온다(프레임마다 레이아웃을 조회하지 않는다).

/** 슬롯: 보스 바 */
export const RESERVE_BOSS = 0;
/** 슬롯: 대상 프레임(방패 고블린·엘리트) */
export const RESERVE_TARGET = 1;
export const RESERVE_SLOTS = 2;

export class ScreenReserve {
  /** 슬롯마다 x0, y0, x1, y1 */
  readonly rect = new Float32Array(RESERVE_SLOTS * 4);
  /** 1 = 화면에 보임 */
  readonly active = new Uint8Array(RESERVE_SLOTS);

  set(slot: number, x0: number, y0: number, x1: number, y1: number): void {
    const o = slot * 4;
    this.rect[o] = x0; this.rect[o + 1] = y0; this.rect[o + 2] = x1; this.rect[o + 3] = y1;
    this.active[slot] = x1 > x0 && y1 > y0 ? 1 : 0;
  }

  clear(slot: number): void { this.active[slot] = 0; }
}

/** 비키기 결과 방향(같은 숫자가 프레임마다 위·아래로 튀지 않게 기억한다) */
export const AVOID_NONE = 0, AVOID_UP = 1, AVOID_DOWN = 2, AVOID_LEFT = 3, AVOID_RIGHT = 4;

/**
 * 아래 가운데 기준점(sx, sy)에 그리는 w×h 상자가 예약 영역과 겹치면 바깥 자리로 옮긴다.
 * 예약 영역은 모두 화면 위쪽 HUD이고 타격 지점은 그 아래 전장이므로 아래(타격 쪽)를 먼저 고른다.
 * 위로 비키면 숫자가 화면 맨 위 HUD 패널 쪽으로 몰렸다(2026-10-15 배치 계측). 아래 자리가 없을 때만 가까운 다른 쪽.
 * 아래로는 "영역 높이 + 숫자 높이"만큼 평행 이동한다: 겹친 숫자 무리의 위아래 간격이 그대로 남는다.
 *   프레임 바로 아래에 맞춰 붙이면 숫자끼리 한 줄로 포개져 보이는 숫자 면적이 거의 늘지 않았다(640 배치 18.6k→19.5k px², 평행 이동은 27.4k).
 *   평행 이동할 자리가 없으면 프레임 바로 아래에 맞춘다.
 * out[0], out[1]에 새 기준점, 반환값은 고른 방향(겹치지 않으면 AVOID_NONE). prefer는 직전 방향(그 방향이 화면 안이면 그대로 쓴다).
 * 숫자를 숨기지 않는다: 언제나 화면 안의 한 자리를 고른다.
 */
export function avoidReserve(r: ScreenReserve, sx: number, sy: number, w: number, h: number, gap: number, viewW: number, viewH: number, prefer: number, out: Float32Array): number {
  let x = sx, y = sy, dir = AVOID_NONE;
  for (let s = 0; s < RESERVE_SLOTS; s++) {
    if (!r.active[s]) continue;
    const o = s * 4;
    const rx0 = r.rect[o] - gap, ry0 = r.rect[o + 1] - gap, rx1 = r.rect[o + 2] + gap, ry1 = r.rect[o + 3] + gap;
    const bx0 = x - w / 2, bx1 = x + w / 2, by0 = y - h, by1 = y;
    if (bx1 <= rx0 || bx0 >= rx1 || by1 <= ry0 || by0 >= ry1) continue;
    // 후보 네 방향의 이동 거리(화면 밖으로 나가는 방향은 제외)
    const up = by1 - ry0, left = bx1 - rx0, right = rx1 - bx0;
    const shift = ry1 - ry0 + h, snap = ry1 - by0;
    const down = y + shift <= viewH ? shift : snap;
    const upOk = ry0 - h >= 0, downOk = ry1 + h <= viewH, leftOk = rx0 - w >= 0, rightOk = rx1 + w <= viewW;
    let pick = AVOID_NONE, best = Infinity;
    if (prefer === AVOID_UP && upOk) pick = AVOID_UP;
    else if (prefer === AVOID_DOWN && downOk) pick = AVOID_DOWN;
    else if (prefer === AVOID_LEFT && leftOk) pick = AVOID_LEFT;
    else if (prefer === AVOID_RIGHT && rightOk) pick = AVOID_RIGHT;
    else if (downOk) pick = AVOID_DOWN;
    else {
      if (upOk && up < best) { best = up; pick = AVOID_UP; }
      if (leftOk && left < best) { best = left; pick = AVOID_LEFT; }
      if (rightOk && right < best) { best = right; pick = AVOID_RIGHT; }
      if (pick === AVOID_NONE) pick = AVOID_DOWN;
    }
    if (pick === AVOID_UP) y -= up;
    else if (pick === AVOID_DOWN) y += down;
    else if (pick === AVOID_LEFT) x -= left;
    else x += right;
    dir = pick;
  }
  out[0] = x; out[1] = y;
  return dir;
}

/** 최종 배치 후보 검사. 보간으로 안전한 위치에서 다시 HUD 안으로 들어가는 것을 막는다. */
export function overlapsReserve(r: ScreenReserve, x: number, y: number, w: number, h: number, gap: number): boolean {
  for (let s = 0; s < RESERVE_SLOTS; s++) {
    if (!r.active[s]) continue;
    const o = s * 4;
    if (x + w / 2 > r.rect[o] - gap && x - w / 2 < r.rect[o + 2] + gap &&
        y > r.rect[o + 1] - gap && y - h < r.rect[o + 3] + gap) return true;
  }
  return false;
}
